import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  Shield,
  Info,
  AlertCircle,
  Search,
  Filter,
  RefreshCw,
  Download,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

interface WindowsEventLog {
  id: number;
  hostname: string;
  event_id: number;
  log_name: string;
  level_name: string;
  provider_name: string;
  message: string;
  user_sid?: string;
  event_time: string;
  received_at: string;
  raw?: any;
}

interface LogStats {
  log_name: string;
  event_count: number;
  critical_count: number;
  error_count: number;
  warning_count: number;
  last_event: string;
}

const CRITICAL_EVENTS = {
  4625: "Failed Logon",
  4740: "Account Locked Out",
  4720: "Account Created",
  4726: "Account Deleted",
  4738: "Account Changed",
  4648: "Explicit Credentials Logon",
  4688: "Process Created",
  4104: "PowerShell Script Block",
  7031: "Service Terminated Unexpectedly",
  7034: "Service Crashed",
  1074: "System Shutdown/Restart",
  4624: "Successful Logon",
  4634: "Account Logged Off",
  5156: "Network Connection Allowed",
  5157: "Network Connection Blocked",
};

const LOG_LEVEL_COLORS = {
  Critical: "destructive",
  Error: "destructive",
  Warning: "secondary",
  Information: "default",
} as const;

const LOG_LEVEL_ICONS = {
  Critical: AlertTriangle,
  Error: AlertCircle,
  Warning: AlertTriangle,
  Information: Info,
};

export default function WindowsLogsPage() {
  const { profile } = useAuth();
  const [logs, setLogs] = useState<WindowsEventLog[]>([]);
  const [stats, setStats] = useState<LogStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLogName, setSelectedLogName] = useState<string>("all");
  const [selectedLevel, setSelectedLevel] = useState<string>("all");
  const [selectedEventId, setSelectedEventId] = useState<string>("all");
  const [selectedHostname, setSelectedHostname] = useState<string>("all");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedLog, setSelectedLog] = useState<WindowsEventLog | null>(null);

  const fetchLogs = async () => {
    if (!profile?.tenant_id) return;

    try {
      setLoading(true);

      let query = supabase
        .from("windows_event_logs")
        .select(
          `
          id,
          hostname,
          event_id,
          log_name,
          level_name,
          provider_name,
          message,
          user_sid,
          event_time,
          received_at,
          raw
        `
        )
        .eq("tenant_id", profile.tenant_id)
        .order("event_time", { ascending: false })
        .limit(1000);

      if (selectedLogName !== "all") {
        query = query.eq("log_name", selectedLogName);
      }
      if (selectedLevel !== "all") {
        query = query.eq("level_name", selectedLevel);
      }
      if (selectedEventId !== "all") {
        query = query.eq("event_id", parseInt(selectedEventId));
      }
      if (selectedHostname !== "all") {
        query = query.eq("hostname", selectedHostname);
      }
      if (searchTerm) {
        query = query.or(
          `message.ilike.%${searchTerm}%,provider_name.ilike.%${searchTerm}%,hostname.ilike.%${searchTerm}%`
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching logs:", error);
        toast.error("Failed to fetch logs");
        return;
      }

      setLogs(data || []);
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!profile?.tenant_id) return;

    try {
      const { data, error } = await supabase.rpc("get_log_statistics", {
        p_tenant_id: profile.tenant_id,
      });

      if (error) {
        console.error("Error fetching stats:", error);
        return;
      }

      setStats(data || []);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const uniqueLogNames = useMemo(() => {
    const names = [...new Set(logs.map((log) => log.log_name))];
    return names.sort();
  }, [logs]);

  const uniqueHostnames = useMemo(() => {
    const hostnames = [...new Set(logs.map((log) => log.hostname))];
    return hostnames.sort();
  }, [logs]);

  const uniqueEventIds = useMemo(() => {
    const eventIds = [...new Set(logs.map((log) => log.event_id))];
    return eventIds.sort((a, b) => a - b);
  }, [logs]);

  useEffect(() => {
    if (!profile?.tenant_id) return;

    const channel = supabase
      .channel("windows-logs-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "windows_event_logs",
          filter: `tenant_id=eq.${profile.tenant_id}`,
        },
        (payload) => {
          console.log("New log received:", payload);
          if (autoRefresh) {
            fetchLogs();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.tenant_id, autoRefresh]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [
    profile?.tenant_id,
    selectedLogName,
    selectedLevel,
    selectedEventId,
    selectedHostname,
    searchTerm,
  ]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchLogs();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const getLevelIcon = (level: string) => {
    const Icon = LOG_LEVEL_ICONS[level as keyof typeof LOG_LEVEL_ICONS] || Info;
    return <Icon className="h-4 w-4" />;
  };

  const getEventDescription = (eventId: number) => {
    return (
      CRITICAL_EVENTS[eventId as keyof typeof CRITICAL_EVENTS] ||
      `Event ${eventId}`
    );
  };

  const formatLogTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const exportLogs = () => {
    const csv = [
      ["Time", "Hostname", "Log Name", "Event ID", "Level", "Source", "Message"].join(","),
      ...logs.map((log) =>
        [
          log.event_time,
          log.hostname,
          log.log_name,
          log.event_id,
          log.level_name,
          log.provider_name,
          `"${log.message.replace(/"/g, '""')}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `windows-logs-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Logs exported successfully");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Windows Event Logs
          </h1>
          <p className="text-muted-foreground">
            ISO27001 compliant monitoring and analysis of Windows Event Logs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-green-50 dark:bg-green-950" : ""}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Auto Refresh {autoRefresh ? "ON" : "OFF"}
          </Button>
          <Button variant="outline" size="sm" onClick={exportLogs}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.log_name}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.log_name}
              </CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stat.event_count.toLocaleString()}
              </div>
              <div className="flex gap-2 text-xs text-muted-foreground mt-2">
                <span className="text-red-600">
                  Critical: {stat.critical_count}
                </span>
                <span className="text-orange-600">
                  Error: {stat.error_count}
                </span>
                <span className="text-yellow-600">
                  Warning: {stat.warning_count}
                </span>
              </div>
              {stat.last_event && (
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  Last: {formatLogTime(stat.last_event)}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={selectedLogName} onValueChange={setSelectedLogName}>
              <SelectTrigger>
                <SelectValue placeholder="Log Name" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Log Names</SelectItem>
                {uniqueLogNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLevel} onValueChange={setSelectedLevel}>
              <SelectTrigger>
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
                <SelectItem value="Error">Error</SelectItem>
                <SelectItem value="Warning">Warning</SelectItem>
                <SelectItem value="Information">Information</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger>
                <SelectValue placeholder="Event ID" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Event IDs</SelectItem>
                {uniqueEventIds.map((id) => (
                  <SelectItem key={id} value={id.toString()}>
                    {id} - {getEventDescription(id)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={selectedHostname}
              onValueChange={setSelectedHostname}
            >
              <SelectTrigger>
                <SelectValue placeholder="Hostname" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hostnames</SelectItem>
                {uniqueHostnames.map((hostname) => (
                  <SelectItem key={hostname} value={hostname}>
                    {hostname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Display */}
      <Card>
        <CardHeader>
          <CardTitle>Event Logs ({logs.length.toLocaleString()} entries)</CardTitle>
          <CardDescription>
            Real-time Windows Event Logs with security focus
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold">No logs found</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                No Windows Event Logs match your current filters
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {getLevelIcon(log.level_name)}
                        <Badge
                          variant={
                            LOG_LEVEL_COLORS[
                              log.level_name as keyof typeof LOG_LEVEL_COLORS
                            ] || "default"
                          }
                          className="shrink-0"
                        >
                          {log.level_name}
                        </Badge>
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium">
                            {formatLogTime(log.event_time)}
                          </span>
                          <Separator orientation="vertical" className="h-4" />
                          <span className="text-muted-foreground">
                            {log.hostname}
                          </span>
                          <Separator orientation="vertical" className="h-4" />
                          <Badge variant="outline" className="shrink-0">
                            {log.event_id} - {getEventDescription(log.event_id)}
                          </Badge>
                          <Separator orientation="vertical" className="h-4" />
                          <span className="text-muted-foreground truncate">
                            {log.log_name}
                          </span>
                        </div>
                        <p className="text-sm mt-1 text-muted-foreground line-clamp-2">
                          {log.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span>Source: {log.provider_name}</span>
                          {log.user_sid && (
                            <>
                              <Separator orientation="vertical" className="h-3" />
                              <span>User: {log.user_sid}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle>Event Log Details</DialogTitle>
                          <DialogDescription>
                            Event ID {log.event_id} -{" "}
                            {getEventDescription(log.event_id)}
                          </DialogDescription>
                        </DialogHeader>
                        <ScrollArea className="max-h-[60vh]">
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-medium">Time</label>
                                <p className="text-sm text-muted-foreground">
                                  {formatLogTime(log.event_time)}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">
                                  Hostname
                                </label>
                                <p className="text-sm text-muted-foreground">
                                  {log.hostname}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">
                                  Event ID
                                </label>
                                <p className="text-sm text-muted-foreground">
                                  {log.event_id}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Level</label>
                                <Badge
                                  variant={
                                    LOG_LEVEL_COLORS[
                                      log.level_name as keyof typeof LOG_LEVEL_COLORS
                                    ] || "default"
                                  }
                                >
                                  {log.level_name}
                                </Badge>
                              </div>
                              <div>
                                <label className="text-sm font-medium">
                                  Log Name
                                </label>
                                <p className="text-sm text-muted-foreground">
                                  {log.log_name}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium">Source</label>
                                <p className="text-sm text-muted-foreground">
                                  {log.provider_name}
                                </p>
                              </div>
                            </div>

                            <div>
                              <label className="text-sm font-medium">Message</label>
                              <Textarea
                                value={log.message}
                                readOnly
                                className="mt-1"
                                rows={4}
                              />
                            </div>

                            {log.raw && (
                              <div>
                                <label className="text-sm font-medium">
                                  Raw Data
                                </label>
                                <Textarea
                                  value={JSON.stringify(log.raw, null, 2)}
                                  readOnly
                                  className="mt-1 font-mono text-xs"
                                  rows={6}
                                />
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
